import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import _ from 'lodash';
import { map, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import {
  IMAGE_SRC,
  VALIDATION_MESSAGE,
} from 'src/app/components/shared/shared-constants';
import { SpecialCharacters } from 'src/app/core/enums';
import {
  Country,
  LocationAddress,
  LocationProfile,
} from 'src/app/core/models/admin';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import {
  ADDRESS_FORM_LABEL,
  ERROR_MESSAGES,
  TABS,
} from '../location-profile.constants';
import {
  AddressFormCopy,
  AddressPropertiesType,
  ContryDropdownOption,
  FORM_CONTROL_VALUES,
  LocationPropertiesType,
} from './address-form.constants';

@Component({
  selector: 'app-address-form',
  templateUrl: './address-form.component.html',
  styleUrls: ['./address-form.component.scss'],
  standalone: false,
})
export class AddressFormComponent implements OnInit {
  @Input() locationItem!: LocationProfile;
  @Input() edit!: boolean;
  @Output() changeTab = new EventEmitter();
  @Output() formStatus = new EventEmitter();

  readonly destroy = inject(DestroyRef);
  readonly CONSTANTS = {
    ...ADDRESS_FORM_LABEL,
    ...VALIDATION_MESSAGE,
    ...ERROR_MESSAGES,
  };
  readonly SpecialCharacters = SpecialCharacters;
  readonly IMAGES = IMAGE_SRC;
  readonly ALPHABET_REGEX = SpecialCharacters.ALPHABET_ONLY;
  readonly FORM_CONTROLS = FORM_CONTROL_VALUES;
  countryList!: Country[];
  iOptionCountries: ContryDropdownOption[] = [];
  formDirty = false;
  formValidAndDirty = false;
  copyOfInitialState: AddressFormCopy = {} as AddressFormCopy;
  addressForm!: FormGroup;
  formState = {
    canSubmit: false,
    canReset: false,
  };

  constructor(
    private readonly fb: FormBuilder,
    public readonly commonUtils: CommonUtils,
    private readonly locationService: LocationService,
    private readonly matDialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.createReturnState();
    this.subscribes();
  }

  checkRouteDeliveryOrder(): boolean {
    return this.locationItem?.routeDeliveryOrder ? true : false;
  }

  checkLatLongValue(value: string): boolean {
    if (value !== undefined && value !== null && value.length > 0) {
      return true;
    }

    return false;
  }

  createReturnState() {
    this.copyOfInitialState = {
      ...this.addressForm.getRawValue(),
    };
  }

  resetAddressForm(): void {
    this.addressForm.reset(this.copyOfInitialState);
    this.locationItem = this.getFormData();
    this.locationService
      .updateLocation(this.locationItem, this.locationItem?.locationCode)
      .pipe(
        take(1),
        map((res) => {
          if (res?.code === 200 && res?.response) {
            this.resetFormState(res?.response);
          }
        }),
      )
      .subscribe();
  }

  getCountryList() {
    this.locationService
      .getAllCountries()
      .pipe(
        take(1),
        map((data) => {
          this.countryList = data?.response?.country || [];
          this.countryList.sort((a, b) => a.isoCode.localeCompare(b.isoCode));
          this.countryList?.forEach((country) => {
            this.iOptionCountries.push({
              label: country.isoCode.toUpperCase(),
              value: country.isoCode.toUpperCase(),
            });
          });
        }),
      )
      .subscribe();
  }

  initForm() {
    this.getCountryList();
    const addressProperties = this.setAddressProperties();

    const locationProperties: LocationPropertiesType = {
      currency: this.locationItem?.currency ?? '',
      latitude: this.locationItem?.latitude ?? '',
      longitude: this.locationItem?.longitude ?? '',
      primaryEmail: this.locationItem?.primaryEmail ?? '',
      primaryName: this.locationItem?.primaryName ?? '',
      primaryPhone: this.locationItem?.primaryPhone ?? '',
      secondaryEmail: this.locationItem?.secondaryEmail ?? '',
      secondaryName: this.locationItem?.secondaryName ?? '',
      secondaryPhone: this.locationItem?.secondaryPhone ?? '',
    };

    const address1 = [
      addressProperties.streetnumber,
      addressProperties.streetprefix,
      addressProperties.streetname,
      addressProperties.streettype,
      addressProperties.streetsuffix,
    ]
      .join(' ')
      .trim();

    const address2 = addressProperties.streetname2;

    this.setAddressForm(
      addressProperties,
      address1,
      address2,
      locationProperties,
    );
  }

  subscribes(): void {
    this.subscribeToEditAddEvents();
    this.addressForm
      .get(FORM_CONTROL_VALUES.COUNTRY)
      ?.valueChanges.pipe(
        takeUntilDestroyed(this.destroy),
        map((countryCode) => {
          const selectedCountry = this.countryList?.find(
            (country) => country.isoCode === countryCode,
          );
          this.addressForm
            .get(FORM_CONTROL_VALUES.CURRENCY)
            ?.setValue(selectedCountry?.defaultCurrency);
        }),
      )
      .subscribe();
  }

  // subscribing to form value changes to determine dirty state of form and enable/disable action buttons accordingly
  subscribeToEditAddEvents(): void {
    this.addressForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((change) => {
        const dirty = !_.isEqual(change, this.copyOfInitialState);
        const valid = this.addressForm.valid;

        this.formDirty = dirty;
        this.formValidAndDirty = dirty && valid;

        this.formState = {
          canReset: this.formDirty,
          canSubmit: this.formValidAndDirty,
        };
        this.formStatus.emit(this.formDirty);
      });
  }

  getFormData(): LocationProfile {
    this.locationItem.address = {
      ...this.locationItem?.address,
      customername:
        this.addressForm.controls[FORM_CONTROL_VALUES.COMPANY_NAME].value,
      streetname: this.addressForm.controls[FORM_CONTROL_VALUES.ADDRESS].value,
      streetname2:
        this.addressForm.controls[FORM_CONTROL_VALUES.ADDRESS_2].value,
      pd1: this.addressForm.controls[FORM_CONTROL_VALUES.STATE].value,
      pd2: this.addressForm.controls[FORM_CONTROL_VALUES.CITY].value,
      postalcode:
        this.addressForm.controls[FORM_CONTROL_VALUES.POSTAL_CODE].value,
      isoCode: this.addressForm.controls[FORM_CONTROL_VALUES.COUNTRY].value,
    } as LocationAddress;

    this.locationItem.currency =
      this.addressForm.controls[FORM_CONTROL_VALUES.CURRENCY].value;
    this.locationItem.latitude =
      this.addressForm.controls[FORM_CONTROL_VALUES.LATITUDE].value;
    this.locationItem.longitude =
      this.addressForm.controls[FORM_CONTROL_VALUES.LONGITUDE].value;
    this.locationItem.primaryName =
      this.addressForm.controls[FORM_CONTROL_VALUES.CONTACT_NAME].value;
    this.locationItem.secondaryName =
      this.addressForm.controls[FORM_CONTROL_VALUES.CONTACT_NAME_2].value;
    this.locationItem.primaryEmail =
      this.addressForm.controls[FORM_CONTROL_VALUES.CONTACT_EMAIL].value;
    this.locationItem.secondaryEmail =
      this.addressForm.controls[FORM_CONTROL_VALUES.CONTACT_EMAIL_2].value;
    this.locationItem.primaryPhone =
      this.addressForm.controls[FORM_CONTROL_VALUES.CONTACT_PHONE].value;
    this.locationItem.secondaryPhone =
      this.addressForm.controls[FORM_CONTROL_VALUES.CONTACT_PHONE_2].value;

    // ✅ Normalize GMT-style timezone before sending (collapse offsets to "GMT")
    this.locationItem.gmtTimeZone = this.normalizeGmtTz(
      this.locationItem.gmtTimeZone,
    );

    return this.locationItem;
  }

  resetFormState(profile: LocationProfile): void {
    // ✅ Normalize inbound profile so offsets don't creep back into state/UI
    profile.gmtTimeZone = this.normalizeGmtTz(profile.gmtTimeZone);

    // update locationItem & state
    if (profile) {
      this.locationItem = profile;
      // Keep `copyOfInitialState` in the flat form-control shape so
      // `addressForm.reset(copyOfInitialState)` continues to work on subsequent resets.
      this.copyOfInitialState = {
        ...this.addressForm.getRawValue(),
      } as AddressFormCopy;
      this.locationService.setProfileState$(this.locationItem);
      this.formDirty = false;
      this.formStatus.emit(this.formDirty);
    }
  }

  submitAddressForm(): void {
    this.locationItem = this.getFormData();
    if (!this.edit) {
      this.changeTab.emit({
        data: { profile: this.locationItem, edit: this.edit },
        tab: TABS.GROUP,
      });
    } else {
      this.locationService
        .updateLocation(this.locationItem, this.locationItem?.locationCode)
        .pipe(
          take(1),
          map((res) => {
            this.matDialog.closeAll();
            if (res?.code === 200 && res?.response) {
              this.resetFormState(res?.response);
              this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
            }
          }),
        )
        .subscribe();
    }
  }

  private setAddressProperties(): AddressPropertiesType {
    return {
      customername: this.locationItem?.address?.customername ?? '',
      streetnumber: this.locationItem?.address?.streetnumber ?? '',
      streetname: this.locationItem?.address?.streetname ?? '',
      streetname2: this.locationItem?.address?.streetname2 ?? '',
      streetprefix: this.locationItem?.address?.streetprefix ?? '',
      streetsuffix: this.locationItem?.address?.streetsuffix ?? '',
      streettype: this.locationItem?.address?.streettype ?? '',
      floor: this.locationItem?.address?.floor ?? '',
      suite: this.locationItem?.address?.suite ?? '',
      pd1: this.locationItem?.address?.pd1 ?? '',
      pd2: this.locationItem?.address?.pd2 ?? '',
      pd3: this.locationItem?.address?.pd3 ?? '',
      postalcode: this.locationItem?.address?.postalcode ?? '',
      isoCode: this.locationItem?.address?.isoCode ?? '',
    };
  }

  private setAddressForm(
    addressProperties: AddressPropertiesType,
    address1: string,
    address2: string,
    locationProperties: LocationPropertiesType,
  ) {
    const isIdoChecked = this.checkRouteDeliveryOrder();
    const latLongPattern = Validators.pattern(
      /^(?:[-+]?((([0-8]?\d)(\.\d+)?)|90(\.0+)?)|[-]?(((1[0-7]\d|0?\d?\d)(\.\d+)?)|180(\.0+)?))$/,
    );
    const latLongValidation = isIdoChecked
      ? [Validators.required, latLongPattern]
      : [latLongPattern];

    this.addressForm = this.fb.group({
      [FORM_CONTROL_VALUES.COMPANY_NAME]: [
        addressProperties.customername,
        Validators.compose([Validators.required, Validators.maxLength(26)]),
      ],
      [FORM_CONTROL_VALUES.ADDRESS]: [
        address1,
        Validators.compose([Validators.required, Validators.maxLength(30)]),
      ],
      [FORM_CONTROL_VALUES.ADDRESS_2]: [
        address2,
        Validators.compose([Validators.maxLength(30)]),
      ],
      [FORM_CONTROL_VALUES.CITY]: [addressProperties.pd2, Validators.required],
      [FORM_CONTROL_VALUES.STATE]: [
        addressProperties.pd1,
        Validators.compose([Validators.required, Validators.maxLength(2)]),
      ],
      [FORM_CONTROL_VALUES.POSTAL_CODE]: [
        addressProperties.postalcode,
        Validators.compose([Validators.required, Validators.maxLength(8)]),
      ],
      [FORM_CONTROL_VALUES.COUNTRY]: [
        addressProperties.isoCode,
        Validators.compose([Validators.required, Validators.maxLength(2)]),
      ],
      [FORM_CONTROL_VALUES.CURRENCY]: [
        locationProperties.currency,
        Validators.compose([Validators.required]),
      ],
      [FORM_CONTROL_VALUES.LATITUDE]: [
        locationProperties.latitude,
        latLongValidation,
      ],
      [FORM_CONTROL_VALUES.LONGITUDE]: [
        locationProperties.longitude,
        latLongValidation,
      ],
      [FORM_CONTROL_VALUES.CONTACT_NAME]: [
        locationProperties.primaryName,
        Validators.compose([Validators.maxLength(50)]),
      ],
      [FORM_CONTROL_VALUES.CONTACT_NAME_2]: [
        locationProperties.secondaryName,
        Validators.compose([Validators.maxLength(50)]),
      ],
      [FORM_CONTROL_VALUES.CONTACT_EMAIL]: [
        locationProperties.primaryEmail,
        Validators.compose([Validators.maxLength(50), Validators.email]),
      ],
      [FORM_CONTROL_VALUES.CONTACT_EMAIL_2]: [
        locationProperties.secondaryEmail,
        Validators.compose([Validators.maxLength(50), Validators.email]),
      ],
      [FORM_CONTROL_VALUES.CONTACT_PHONE]: [
        locationProperties.primaryPhone,
        Validators.compose([Validators.maxLength(25)]),
      ],
      [FORM_CONTROL_VALUES.CONTACT_PHONE_2]: [
        locationProperties.secondaryPhone,
        Validators.compose([Validators.maxLength(25)]),
      ],
    });
  }

  private normalizeGmtTz(input?: string): string | undefined {
    if (!input) return input;
    const trimmed = input.trim();
    // Collapse any "GMT", "GMT+/-offset" to just "GMT"
    if (/^GMT/i.test(trimmed)) {
      return 'GMT';
    }
    // For strings like "America/New_York (Eastern Time)" -> keep "America/New_York"
    return trimmed.split(/\s|\(/)[0];
  }
}
